import React, { forwardRef, ForwardRefRenderFunction } from 'react'
import {
  InputProps as MUIInputProps,
  AutocompleteRenderInputParams,
  InputAdornment
} from '@mui/material'
import {
  TextFieldStyled,
  SearchIconStyled,
  ErrorIconStyled,
  WrapLabelStyled,
  FormControlStyled,
  PercentStyled,
  MoneyStyled,
  LabelAdornmentStyled,
  MaxLengthStyled,
  LabelRow
} from './styles'
import { Info, Label, TextButton } from 'components'

export enum DSInputType {
  MASTER_INPUT = 'hav-master-field-input',
  TEXT_INPUT = 'hav-text-field-input',
  SEARCH_INPUT = 'hav-search-field-input',
  PERCENT_INPUT = 'hav-percent-field-input',
  CURRENCY_INPUT = 'hav-currency-field-input'
}

export interface InputProps {
  design?: DSInputType
  name?: string
  linkLabel?: string
  id?: string
  autocompleteParams?: AutocompleteRenderInputParams
  value: string | number
  label?: string
  info?: string
  disabled?: boolean
  type?: 'text' | 'number' | 'password' | 'email'
  InputProps?: MUIInputProps
  onClick?: React.MouseEventHandler<HTMLDivElement>
  addormentOnClick?: React.MouseEventHandler<SVGSVGElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  addorment?: boolean
  error?: string | null
  errorOutline?: boolean
  required?: boolean
  width?: number
  height?: number
  rows?: number
  autoSize?: boolean
  placeholder?: string
  textUppercase?: boolean
  browserAutocomplete?: 'on' | 'off'
  showMaxLength?: boolean
  maxLength?: number
  maxLenghtPosition?: 'top' | 'bottom'
  labelAdornment?: React.ReactNode
  helperText?: string
  successOutline?: boolean
  hasWarning?: boolean
}

// TODO: Refactor this component to reduce the number of props
const InputComponent: ForwardRefRenderFunction<HTMLInputElement, InputProps> = (
  {
    design = DSInputType.MASTER_INPUT,
    autocompleteParams = null,
    name = undefined,
    linkLabel,
    disabled,
    id,
    value = '',
    label,
    info,
    type = 'text',
    InputProps,
    onBlur = () => {
      return
    },
    onFocus = () => {
      return
    },
    onClick = () => {
      return
    },
    addormentOnClick,
    onChange,
    onKeyDown,
    addorment = false,
    required = false,
    error = null,
    errorOutline = false,
    hasWarning = false,
    width,
    height = 30,
    rows = 1,
    placeholder,
    textUppercase = false,
    browserAutocomplete = 'on',
    maxLength,
    maxLenghtPosition = 'bottom',
    showMaxLength = false,
    labelAdornment,
    autoSize = false,
    helperText,
    successOutline
  },
  ref
) => {
  const hasError = () => {
    return error !== '' && error !== null
  }
  const isAutoSize = autoSize ? { minRows: rows } : { rows, height }

  const hasAddorment = () => {
    return addorment || hasError()
  }

  const renderIcon = (dsInputType: DSInputType) =>
    hasAddorment() ? (
      <InputAdornment position="end">
        {dsInputType === DSInputType.SEARCH_INPUT ? (
          <SearchIconStyled
            data-testid="icon-search"
            style={addormentOnClick ? { cursor: 'pointer' } : { cursor: 'auto' }}
            onClick={addormentOnClick}
          />
        ) : hasError() ? (
          <ErrorIconStyled data-testid="icon-error" />
        ) : dsInputType === DSInputType.PERCENT_INPUT ? (
          <PercentStyled data-testid="icon-percent" />
        ) : dsInputType === DSInputType.CURRENCY_INPUT ? (
          <MoneyStyled data-testid="icon-money" />
        ) : null}
      </InputAdornment>
    ) : null
  return (
    <>
      {label && (
        <LabelRow>
          <WrapLabelStyled disabled={disabled} hasInfo={!!info}>
            <Label id="LabelInputForm" htmlFor={name} required={required}>
              {label}
            </Label>
            {!!info && <Info>{info}</Info>}
            {linkLabel && (
              <TextButton url={linkLabel} external>
                Ver Tabela
              </TextButton>
            )}
            {labelAdornment && (
              <LabelAdornmentStyled>{labelAdornment}</LabelAdornmentStyled>
            )}
          </WrapLabelStyled>
          {showMaxLength && maxLength && maxLenghtPosition === 'top' && (
            <MaxLengthStyled position={maxLenghtPosition}>
              {value?.toString()?.length}/{maxLength}
            </MaxLengthStyled>
          )}
        </LabelRow>
      )}
      <FormControlStyled disabled={disabled} fullWidth {...(width && { width })}>
        <TextFieldStyled
          inputRef={ref}
          textuppercase={textUppercase}
          disabled={disabled}
          design={design}
          error={hasError() || errorOutline}
          hasWarning={hasWarning}
          {...autocompleteParams}
          variant="outlined"
          name={name}
          helperText={helperText}
          successoutline={successOutline}
          {...isAutoSize}
          multiline={rows > 1 || autoSize}
          hasmultiline={rows > 1 || autoSize ? 1 : undefined}
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={onClick}
          autoComplete={browserAutocomplete}
          id={name}
          value={value}
          type={type}
          onChange={onChange}
          onKeyDown={onKeyDown}
          addorment={hasAddorment() ? 1 : undefined}
          InputLabelProps={{ shrink: false }}
          InputProps={{
            ...InputProps,
            id: id,
            ...{
              endAdornment:
                InputProps?.endAdornment === undefined
                  ? renderIcon(design)
                  : InputProps?.endAdornment,
              'data-testid': 'input-component'
            },
            inputProps: {
              maxLength
            }
          }}
          placeholder={placeholder}
          {...(error && { error: true, helperText: error })}
        />
        {showMaxLength && maxLength && maxLenghtPosition === 'bottom' && (
          <MaxLengthStyled position={maxLenghtPosition}>
            {value?.toString()?.length}/{maxLength}
          </MaxLengthStyled>
        )}
      </FormControlStyled>
    </>
  )
}

export const Input = forwardRef(InputComponent)
